import React, { useState, useEffect, useRef } from 'react';
import { useAdminUsers } from '../context/AdminUsersContext';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import {
    Container,
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableData,
    EditButton,
    PaginationContainer,
    PageButton,
    SearchInput,
    FilterLabel,
    TableWrapper,
    RowsSelect,
    PageSelect,
    ContentWrapper,
    SyncWrap,
    SyncButton,
    ControlBar,
    StickyFilterBar,
    HostifyInput,
    SyncHostifyButton,
    ErrorPopup,
    CloseButton,
    RolePill,
    TableCheckbox,
    SearchWrap,
    SyncGroup
} from '../style-comps/UserListing.styles';
import { useNavigate } from 'react-router-dom';
import { getUsers, getListingsByUserId, getUserById, syncAdminUserListingsWithUserData, syncAdminListings, syncAdminUsersNew, syncUserByHostifyId, syncAdminListingById, getUserRoles, batchSetTestUser } from '../services/api';
import { ModelLoadingWrapper } from '../style-comps/DashboardHome.styles';
import syncIcon from '../Assets/arrow-round-left.png'

const UserListing = () => {
    const { users, setUsers, hasFetched, setHasFetched, refetchUsers, userRolesMap, setUserRolesMap } = useAdminUsers();
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [onlyWithListings, setOnlyWithListings] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [progress, setProgress] = useState(0);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [searchParams, setSearchParams] = useSearchParams();
    const initialPage = parseInt(searchParams.get('page')) || 1;
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [hostifyId, setHostifyId] = useState('');
    const [listingSyncId, setListingSyncId] = useState('');
    const [usersPerPage, setUsersPerPage] = useState(10);
    const progressTimerRef = useRef(null);
    const isFirstRenderRef = useRef(true);
    // 追蹤目前這一輪抓取；離開頁面或重新抓取時讓舊的背景補抓停止
    const fetchSeqRef = useRef(0);
    const backfillRunningRef = useRef(false);
    const location = useLocation();
    const hasFetchedRef = useRef(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [onlyTestUsers, setOnlyTestUsers] = useState(false);
    const [isBatchUpdating, setIsBatchUpdating] = useState(false);
    const [originFilter, setOriginFilter] = useState('all');




    useEffect(() => {
        if (localStorage.getItem('requirePasswordChange') === 'true') {
            setIsLoading(false);
            return;                       // 強制改密碼期間不要抓 admin 資料
        }
        if (!hasFetched) {
            fetchAndSetUsers();
        } else if (location.state?.shouldRefetch) {
            const changedUserId = location.state.changedUserId;
            if (changedUserId) {
                const hasUpdatedRoles = Array.isArray(location.state?.updatedRoles) && location.state.updatedRoles.length > 0;

                if (hasUpdatedRoles) {
                    // 先用 navigate state 立即更新 role（畫面馬上反應）
                    setUserRolesMap(prev => ({
                        ...prev,
                        [changedUserId]: location.state.updatedRoles
                    }));
                    // 再更新 listing count，但跳過 roles API（避免覆蓋）
                    refreshSingleUser(changedUserId, true);  // ← skipRoles = true
                } else {
                    refreshSingleUser(changedUserId, false);
                }
            } else {
                fetchAndSetUsers();
            }
            navigate(location.pathname + location.search, { replace: true });
        } else {
            // 沒改動 → 不抓，直接用 Context 的 users
            setIsLoading(false);

            // 上次的背景補抓若被中斷（切頁），為還缺 count / roles 的使用者續補
            if (!backfillRunningRef.current) {
                const pending = users.filter(
                    u => u.listingCount == null || userRolesMap[u.Id] === undefined
                );
                if (pending.length > 0) {
                    fetchSeqRef.current += 1;
                    backfillUserDetails(pending, fetchSeqRef.current);
                }
            }
        }
    }, [hasFetched, location.state, navigate, location.pathname, location.search]);

    // Loading 提取共同作業
    const startLoading = () => {
        if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
        }

        setIsLoading(true);
        setFadeOut(false);
        setProgress(0);

        let percent = 0;
        const id = setInterval(() => {
            percent += Math.floor(Math.random() * 10) + 5;
            percent = Math.min(percent, 99);
            setProgress(percent);
        }, 300);

        progressTimerRef.current = id;
    };

    const stopLoading = () => {
        if (progressTimerRef.current) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
        }
        setProgress(100);
        setFadeOut(true);
        setTimeout(() => setIsLoading(false), 600);
    };

    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
        return () => clearTimeout(id);
    }, [searchTerm]);

    useEffect(() => {
        // 下一個事件循環後把 firstRender 關掉，避免首 render 的其它 effect 參與 reset
        const t = setTimeout(() => { isFirstRenderRef.current = false; }, 0);
        return () => clearTimeout(t);
    }, []);


    useEffect(() => {
        return () => {
            if (progressTimerRef.current) {
                clearInterval(progressTimerRef.current);
            }
            // 離開頁面：讓進行中的背景補抓在下一批之前停止，釋放連線給其他頁面
            fetchSeqRef.current += 1;
        };
    }, []);


    // 背景分批補抓每個使用者的 listingCount 與 roles。
    // 一次最多 5 個使用者（= 10 個並發請求）：佇列深度有限，
    // 其他頁面（Property Listing / Roles & Permissions / User Logs）的 API 最多等一輪就能插入。
    const backfillUserDetails = async (targetUsers, seq) => {
        const token = localStorage.getItem('authToken');
        const CHUNK_SIZE = 5;
        backfillRunningRef.current = true;

        try {
            for (let i = 0; i < targetUsers.length; i += CHUNK_SIZE) {
                if (seq !== fetchSeqRef.current) return;   // 已離開頁面或有新一輪抓取

                const chunk = targetUsers.slice(i, i + CHUNK_SIZE);
                const results = await Promise.all(
                    chunk.map(async (u) => {
                        const [listingCount, roles] = await Promise.all([
                            getListingsByUserId(u.Id, token)
                                .then(listings => (Array.isArray(listings) ? listings.length : 0))
                                .catch(() => 0),
                            getUserRoles(u.Id)
                                .then(rs => rs.map(r => r.RoleName || r.Name).filter(Boolean))
                                .catch(() => []),
                        ]);
                        return { id: u.Id, listingCount, roles };
                    })
                );

                if (seq !== fetchSeqRef.current) return;

                setUsers(prev =>
                    prev.map(u => {
                        const r = results.find(x => x.id === u.Id);
                        return r ? { ...u, listingCount: r.listingCount } : u;
                    })
                );
                setUserRolesMap(prev => {
                    const next = { ...prev };
                    results.forEach(r => { next[r.id] = r.roles; });
                    return next;
                });
            }
        } finally {
            backfillRunningRef.current = false;
        }
    };

    const fetchAndSetUsers = async () => {
        fetchSeqRef.current += 1;
        const seq = fetchSeqRef.current;

        startLoading();
        try {
            const rawUsers = await getUsers();
            if (seq !== fetchSeqRef.current) return;

            // 先讓表格出來（Listings 欄先顯示 -），counts / roles 背景分批補
            setUsers(rawUsers.map(u => ({ ...u, listingCount: u.listingCount ?? null })));
            setHasFetched(true);
            stopLoading();

            await backfillUserDetails(rawUsers, seq);
        } catch (e) {
            console.error('🔴 無法取得使用者資料:', e);
            if (seq === fetchSeqRef.current) stopLoading();
        }
    };



    const handlePageChange = (page) => {
        const next = Math.max(1, Number(page) || 1);
        if (next === currentPage) return; // 同頁就不動
        setCurrentPage(next);
        setSearchParams(prev => {
            const sp = new URLSearchParams(prev);
            sp.set('page', String(next));
            return sp;
        }, { replace: true });
    };

    const handleItemsPerPageChange = (e) => {
        const next = parseInt(e.target.value, 10) || 10;
        setUsersPerPage(next);
        // 換每頁數量時回到第 1 頁
        setCurrentPage(1);
        setSearchParams({ page: 1 }, { replace: true });
    };



    useEffect(() => {
        if (isFirstRenderRef.current) return;
        if (debouncedSearchTerm !== "") {
            setCurrentPage(1);
            setSearchParams({ page: 1 });
        }
    }, [debouncedSearchTerm, setSearchParams]);



    const isHostifyOrigin = (user) =>
        String(user.Origin || '').toLowerCase() === 'hostify';

    const filterUsers = (searchText, allUsers, onlyList, rolesMap, onlyTest, origin) => {
        const lower = searchText.toLowerCase();
        const filtered = allUsers.filter(user => {
            const roles = rolesMap?.[user.Id] ?? [];
            const matchText =
                user.HostifyId?.toString().includes(lower) ||
                (user.UserName?.toLowerCase().includes(lower) ?? false) ||
                (user.FirstName?.toLowerCase().includes(lower) ?? false) ||
                (user.LastName?.toLowerCase().includes(lower) ?? false) ||
                user.Id?.toString().includes(lower) ||
                roles.some(role => role.toLowerCase().includes(lower));
            const matchListing = !onlyList || (user.listingCount || 0) > 0;
            const matchTest = !onlyTest || user.IsTestUser === true;
            const matchOrigin =
                origin === 'all' ||
                (origin === 'hostify' && isHostifyOrigin(user)) ||
                (origin === 'local' && !isHostifyOrigin(user));
            return matchText && matchListing && matchTest && matchOrigin;
        });
        setFilteredUsers(filtered);
    };

    const handleSyncListings = async () => {
        startLoading();
        try {
            const result = await syncAdminListings();
            const msg = `Sync Listings successfully: ${result?.message || 'Operation completed successfully.'}`;
            console.log(msg);
            setErrorMessage(msg);
            setShowError(true);
            await fetchAndSetUsers();
        } catch (err) {
            const errMsg = `Sync Listings failed: ${err?.response?.data?.message || err.message}`;
            console.error(errMsg);
            setErrorMessage(errMsg);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };


    const handleSyncUserListings = async () => {
        startLoading();
        try {
            const result = await syncAdminUserListingsWithUserData();


            let messageText = result?.message || result?.Message || '';
            const match = messageText.match(/(\d+)\s+new user-listing relations/);
            const count = match ? Number(match[1]) : null;


            const msg = count !== null
                ? `Sync User Listings successfully: ${count} user-listing relations synced successfully.`
                : `Sync User Listings successfully: ${messageText || 'All user listings synced successfully.'}`;

            console.log(msg);
            setErrorMessage(msg);
            setShowError(true);
        } catch (err) {
            const errMsg = `Sync User Listings failed: ${err?.response?.data?.message || err.message}`;
            console.error(errMsg);
            setErrorMessage(errMsg);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };




    const handleSyncNewUsers = async () => {
        startLoading();
        try {
            const result = await syncAdminUsersNew();
            let messageText = result?.message || result?.Message || '';
            const match = messageText.match(/(\d+)\s+new users/);
            const count = match ? Number(match[1]) : null;

            const msg = count !== null
                ? `Sync New Users successfully: ${count} new users synced successfully.`
                : `Sync New Users successfully: ${messageText || '0 new users.'}`;

            console.log(msg);
            setErrorMessage(msg);
            setShowError(true);
            await fetchAndSetUsers();
        } catch (err) {
            const errMsg = `Sync New Users failed: ${err?.response?.data?.message || err.message}`;
            console.error(errMsg);
            setErrorMessage(errMsg);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };



    const handleSyncByHostifyId = async () => {
        if (!hostifyId.trim()) {
            console.warn(" HostifyId 為空");
            return;
        }
        startLoading();
        try {
            const result = await syncUserByHostifyId(hostifyId.trim());
            console.log("成功同步 Hostify 使用者:", result);

            // ✅ 先顯示成功 popup
            setErrorMessage(`Sync Hostify ID successfully (${hostifyId.trim()})`);
            setShowError(true);

            // ✅ 再重新整理列表
            await fetchAndSetUsers();

            setHostifyId('');
        } catch (error) {
            console.error("Sync Hostify failed:", error?.response?.data?.message || error.message);
            setErrorMessage(`Sync Hostify ID failed: ${error?.response?.data?.message || error.message}`);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };


    const handleSyncListingById = async () => {
        if (!listingSyncId.trim()) {
            console.warn("⚠️ Listing ID 為空");
            return;
        }
        startLoading();
        try {
            const result = await syncAdminListingById(listingSyncId.trim());
            console.log("成功同步 Listing:", result);


            setErrorMessage(`Sync Listing ID successfully (${listingSyncId.trim()})`);
            setShowError(true);

            await fetchAndSetUsers();

            setListingSyncId('');
        } catch (error) {
            console.error("Sync Listing failed:", error?.response?.data?.message || error.message);
            setErrorMessage(`Sync Listing ID failed: ${error?.response?.data?.message || error.message}`);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };




    const refreshSingleUser = async (userId, skipRoles = false) => {
        try {
            const token = localStorage.getItem('authToken');

            const listings = await getListingsByUserId(userId, token);
            const count = Array.isArray(listings) ? listings.length : 0;
            setUsers(prev =>
                prev.map(u =>
                    String(u.Id) === String(userId) ? { ...u, listingCount: count } : u
                )
            );

            if (!skipRoles) {
                const rolesData = await getUserRoles(userId);
                setUserRolesMap(prev => ({
                    ...prev,
                    [userId]: rolesData.map(r => r.RoleName || r.Name).filter(Boolean)
                }));
            }

        } catch (e) {
            console.error('⚠️ 無法更新單一使用者:', e);
        } finally {
            setIsLoading(false);
        }
    };



    useEffect(() => {
        filterUsers(debouncedSearchTerm, users, onlyWithListings, userRolesMap, onlyTestUsers, originFilter);
    }, [debouncedSearchTerm, users, onlyWithListings, userRolesMap, onlyTestUsers, originFilter]);

    //用 navigate 的 state 傳遞選取的 user
    const handleEditUser = (user) => {
        navigate(`/dashboard/user-setting/${user.Id}`, {
            state: { user, fromPage: currentPage }
        });
    };

    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // ===== 批次選取（Set 存 user ID，翻頁不會遺失） =====
    const toggleSelectUser = (userId) => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    };

    const isAllCurrentPageSelected =
        currentUsers.length > 0 && currentUsers.every(u => selectedUserIds.has(u.Id));

    const toggleSelectAllCurrentPage = () => {
        setSelectedUserIds(prev => {
            const next = new Set(prev);
            if (isAllCurrentPageSelected) {
                currentUsers.forEach(u => next.delete(u.Id));
            } else {
                currentUsers.forEach(u => next.add(u.Id));
            }
            return next;
        });
    };

    // ===== 批次設定/取消測試帳號 =====
    const handleBatchTestUser = async (isTestUser) => {
        if (selectedUserIds.size === 0 || isBatchUpdating) return;

        const ids = Array.from(selectedUserIds);
        const currentUserId = localStorage.getItem('userId');
        const includesSelf = ids.some(id => String(id) === String(currentUserId));

        const baseMsg = isTestUser
            ? `Set ${ids.length} user(s) as test users?`
            : `Remove test-user flag from ${ids.length} user(s)?`;
        const selfWarn = includesSelf
            ? '\n\n⚠️ Your own account is included in this selection.'
            : '';
        if (!window.confirm(baseMsg + selfWarn)) return;

        setIsBatchUpdating(true);
        try {
            await batchSetTestUser(ids, isTestUser);

            // 直接更新本地資料，不重抓整個列表
            setUsers(prev =>
                prev.map(u =>
                    selectedUserIds.has(u.Id) ? { ...u, IsTestUser: isTestUser } : u
                )
            );
            setErrorMessage(
                isTestUser
                    ? `${ids.length} user(s) set as test users.`
                    : `${ids.length} user(s) removed from test users.`
            );
            setShowError(true);
            setSelectedUserIds(new Set());
        } catch (err) {
            setErrorMessage(`Batch update failed: ${err?.response?.data?.message || err.message}`);
            setShowError(true);
        } finally {
            setIsBatchUpdating(false);
        }
    };

    useEffect(() => {
        if (!isLoading && totalPages > 0 && currentPage > totalPages) {
        }
    }, [totalPages, currentPage, isLoading]);

    useEffect(() => {
        const raw = searchParams.get("page");
        const pageParam = Math.max(1, parseInt(raw || "1", 10) || 1);
        if (pageParam !== currentPage) {
            setCurrentPage(pageParam);
        }
    }, [searchParams, currentPage]);

    if (isLoading) {
        return (
            <ModelLoadingWrapper fadeOut={fadeOut}>
                <p style={{ fontSize: '20px', color: '#61CBC9', fontWeight: 'bold' }}>
                    Loading Users... {Math.min(progress, 100)}%
                </p>
                <model-viewer
                    src="/model/koala_3d_head.glb"
                    alt="Loading"
                    auto-rotate
                    camera-controls
                    style={{ width: '150px', height: '150px' }}
                />
            </ModelLoadingWrapper>
        );
    }



    const getPaginationPages = () => {
        const pages = [];
        const maxPagesToShow = 4;

        let start = Math.max(1, currentPage);
        let end = Math.min(start + maxPagesToShow - 1, totalPages);

        if (end - start + 1 < maxPagesToShow && start > 1) {
            start = Math.max(1, end - maxPagesToShow + 1);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    };



    return (
        <Container>
            <StickyFilterBar>
            <ControlBar>
                <SearchWrap>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round">
                        <circle cx="11" cy="11" r="7" />
                        <line x1="21" y1="21" x2="16.5" y2="16.5" />
                    </svg>
                    <SearchInput
                        type="text"
                        placeholder="Search by ID, property or username"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </SearchWrap>

                <FilterLabel>
                    <input
                        type="checkbox"
                        checked={onlyWithListings}
                        onChange={(e) => setOnlyWithListings(e.target.checked)}
                    />
                    Only users with listings
                </FilterLabel>

                <FilterLabel>
                    <input
                        type="checkbox"
                        checked={onlyTestUsers}
                        onChange={(e) => setOnlyTestUsers(e.target.checked)}
                    />
                    Only test users
                </FilterLabel>
            </ControlBar>

            <ControlBar>
                <FilterLabel as="div" style={{ cursor: 'default' }}>
                    Origin
                    <RowsSelect
                        value={originFilter}
                        onChange={(e) => setOriginFilter(e.target.value)}
                    >
                        <option value="all">All</option>
                        <option value="hostify">Hostify</option>
                        <option value="local">Local</option>
                    </RowsSelect>
                </FilterLabel>

                <SyncGroup>
                    <HostifyInput
                        type="text"
                        value={hostifyId}
                        placeholder="Hostify ID"
                        onChange={(e) => setHostifyId(e.target.value)}
                    />
                    <SyncHostifyButton onClick={handleSyncByHostifyId}>
                        Sync
                    </SyncHostifyButton>
                </SyncGroup>
                <SyncGroup>
                    <HostifyInput
                        type="text"
                        value={listingSyncId}
                        placeholder="Listing ID"
                        onChange={(e) => setListingSyncId(e.target.value)}
                    />
                    <SyncHostifyButton onClick={handleSyncListingById}>
                        Sync
                    </SyncHostifyButton>
                </SyncGroup>
            </ControlBar>
            </StickyFilterBar>

            {selectedUserIds.size > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    background: '#eaf7f7',
                    border: '1px solid #61CBC9',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    margin: '10px 0'
                }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#2b7c7a' }}>
                        {selectedUserIds.size} selected
                    </span>
                    <SyncHostifyButton
                        onClick={() => handleBatchTestUser(true)}
                        disabled={isBatchUpdating}
                    >
                        Set as Test User
                    </SyncHostifyButton>
                    <SyncHostifyButton
                        onClick={() => handleBatchTestUser(false)}
                        disabled={isBatchUpdating}
                    >
                        Remove Test User
                    </SyncHostifyButton>
                    <button
                        onClick={() => setSelectedUserIds(new Set())}
                        style={{
                            marginLeft: 'auto',
                            background: 'none',
                            border: 'none',
                            color: '#777',
                            cursor: 'pointer',
                            fontSize: 13
                        }}
                    >
                        ✕ Clear
                    </button>
                </div>
            )}
            <ContentWrapper>
                <TableWrapper>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader style={{ width: '4%' }}>
                                    <TableCheckbox
                                        checked={isAllCurrentPageSelected}
                                        onChange={toggleSelectAllCurrentPage}
                                        title="Select all on this page"
                                    />
                                </TableHeader>
                                <TableHeader style={{ width: '5%' }}>ID</TableHeader>
                                <TableHeader style={{ width: '10%' }}>Hostify ID</TableHeader>
                                <TableHeader style={{ width: '18%' }}>Username</TableHeader>
                                <TableHeader style={{ width: '14.28%' }}>First Name</TableHeader>
                                <TableHeader style={{ width: '14.28%' }}>Last Name</TableHeader>
                                <TableHeader style={{ width: '10%' }}>Role</TableHeader>
                                <TableHeader style={{ width: '14.28%' }}>Listings</TableHeader>
                                <TableHeader style={{ width: '14.28%' }}>Actions</TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentUsers.map((user, index) => (
                                <TableRow
                                    key={user.Id || index}
                                    style={selectedUserIds.has(user.Id) ? { background: '#eaf7f7' } : undefined}
                                >
                                    <TableData>
                                        <TableCheckbox
                                            checked={selectedUserIds.has(user.Id)}
                                            onChange={() => toggleSelectUser(user.Id)}
                                        />
                                    </TableData>
                                    <TableData>{user.Id}</TableData>
                                    <TableData>{user.HostifyId}</TableData>
                                    <TableData>
                                        {user.UserName}
                                        {user.IsTestUser === true && (
                                            <span style={{
                                                marginLeft: 6,
                                                background: '#fff3cd',
                                                color: '#8a6d1a',
                                                borderRadius: '10px',
                                                padding: '1px 8px',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                TEST
                                            </span>
                                        )}
                                        {isHostifyOrigin(user) ? (
                                            <span style={{
                                                marginLeft: 6,
                                                background: '#e8f1fd',
                                                color: '#2d6cb5',
                                                borderRadius: '10px',
                                                padding: '1px 8px',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                HOSTIFY
                                            </span>
                                        ) : (
                                            <span style={{
                                                marginLeft: 6,
                                                background: '#ececec',
                                                color: '#666',
                                                borderRadius: '10px',
                                                padding: '1px 8px',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                LOCAL
                                            </span>
                                        )}
                                    </TableData>
                                    <TableData>{user.FirstName}</TableData>
                                    <TableData>{user.LastName}</TableData>
                                    <TableData>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                                            {(userRolesMap[user.Id] ?? []).map((role) => (
                                                <RolePill key={role}>{role}</RolePill>
                                            ))}
                                        </div>
                                    </TableData>
                                    <TableData>{user.listingCount ?? '-'}</TableData>
                                    <TableData>

                                        <EditButton
                                            as={Link}
                                            to={{
                                                pathname: `/dashboard/user-setting/${user.Id}`,
                                                search: searchParams.toString()
                                                    ? `?from=${encodeURIComponent(searchParams.toString())}`
                                                    : ''
                                            }}
                                            state={{ user, fromQuery: searchParams.toString() }}
                                        >
                                            Edit
                                        </EditButton>
                                    </TableData>
                                </TableRow>
                            ))}
                        </TableBody>

                    </Table>
                </TableWrapper>
            </ContentWrapper>
            <PaginationContainer>
                {/* 左邊：Rows per page */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, color: "#777" }}>Rows per page:</span>
                    <RowsSelect value={usersPerPage} onChange={handleItemsPerPageChange}>
                        {[10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </RowsSelect>
                </div>

                {/* 右邊：頁碼控制 */}


                {/* 中間：PageButton & PageSelect 置中 */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", flexGrow: 1 }}>
                    <PageButton
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                    >
                        ←
                    </PageButton>

                    <PageSelect
                        value={currentPage}
                        onChange={(e) => handlePageChange(Number(e.target.value))}
                    >
                        {Array.from({ length: totalPages }, (_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {i + 1}
                            </option>
                        ))}
                    </PageSelect>

                    <PageButton
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                    >
                        →
                    </PageButton>
                </div>

                {/* 右邊：PageSelect ＆ 筆數 */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 13, color: "#777" }}>
                        {(debouncedSearchTerm || onlyWithListings || onlyTestUsers || originFilter !== 'all')
                            ? `${filteredUsers.length} results`
                            : `${users.length} total`}
                    </span>
                </div>
            </PaginationContainer>





            <SyncWrap>
                <SyncButton onClick={handleSyncListings}>
                    <img src={syncIcon} alt="sync icon" />
                    Listings
                </SyncButton>

                <SyncButton onClick={handleSyncUserListings}>
                    <img src={syncIcon} alt="sync icon" />
                    User Listings
                </SyncButton>

                <SyncButton onClick={handleSyncNewUsers}>
                    <img src={syncIcon} alt="sync icon" />
                    Sync new user
                </SyncButton>
            </SyncWrap>

            {showError && (
                <ErrorPopup show={showError}>
                    <div>{errorMessage}</div>
                    <CloseButton onClick={() => setShowError(false)}>×</CloseButton>
                </ErrorPopup>
            )}

        </Container>
    );
};

export default UserListing;